// Revenue view (Section 5.5): weighted projected commissions by month or quarter,
// split into received / invoiced / projected, with drill-down to the deals behind
// each number. Reads the revenue_projection view — the same numbers the digest and
// assistant will use.

import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Card, cx, Empty, ErrorBox, PageHeader, Pill, Spinner, Stat } from '../components/ui';
import { DEAL_TYPE_LABEL } from '../lib/deals';
import { money } from '../lib/format';
import { supabase, unwrap } from '../lib/supabase';

type Line = { payment_id: string; deal_id: string; deal_name: string; deal_type: string; category: string; month_start: string | null; amount: number; weighted_amount: number; probability: number };

function bucketOf(monthStart: string | null, by: 'month' | 'quarter'): string {
  if (!monthStart) return 'unscheduled';
  if (by === 'month') return monthStart.slice(0, 7);
  const m = Number(monthStart.slice(5, 7));
  return `${monthStart.slice(0, 4)}-Q${Math.ceil(m / 3)}`;
}
function bucketLabel(b: string): string {
  if (b === 'unscheduled') return 'Date not set';
  if (b.includes('Q')) return `${b.slice(5)} ${b.slice(0, 4)}`;
  const [y, m] = b.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, 1)).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

export function Revenue() {
  const [by, setBy] = useState<'month' | 'quarter'>('month');
  const [open, setOpen] = useState<string | null>(null);
  const { data = [], isLoading, error } = useQuery({
    queryKey: ['revenue'],
    queryFn: async () => unwrap(await supabase.from('revenue_projection').select('payment_id, deal_id, deal_name, deal_type, category, month_start, amount, weighted_amount, probability')) as Line[],
  });

  const buckets = useMemo(() => {
    const map = new Map<string, { received: number; invoiced: number; projected: number; lines: Line[] }>();
    for (const l of data) {
      const k = bucketOf(l.month_start, by);
      const b = map.get(k) ?? { received: 0, invoiced: 0, projected: 0, lines: [] };
      b[l.category as 'received' | 'invoiced' | 'projected'] += Number(l.weighted_amount);
      b.lines.push(l);
      map.set(k, b);
    }
    return [...map.entries()].sort(([a], [b]) => (a === 'unscheduled' ? 1 : b === 'unscheduled' ? -1 : a.localeCompare(b)));
  }, [data, by]);

  const year = new Date().getFullYear().toString();
  const totals = data.reduce(
    (t, l) => {
      const inYear = l.month_start?.startsWith(year);
      if (l.category === 'received' && inYear) t.receivedYtd += Number(l.weighted_amount);
      if (l.category !== 'received') {
        t.pipelineWeighted += Number(l.weighted_amount);
        t.pipelineFull += Number(l.amount);
      }
      return t;
    },
    { receivedYtd: 0, pipelineWeighted: 0, pipelineFull: 0 },
  );
  const max = Math.max(1, ...buckets.map(([, b]) => b.received + b.invoiced + b.projected));

  if (isLoading) return <Spinner />;
  return (
    <div>
      <PageHeader
        title="Revenue"
        subtitle="Received counts at 100% in the month received. Everything else is weighted by the deal's current stage probability."
        actions={
          <div className="flex rounded-lg bg-slate-100 p-1">
            {(['month', 'quarter'] as const).map((b) => (
              <button key={b} onClick={() => setBy(b)} className={cx('rounded-md px-3 py-1 text-sm font-medium capitalize', by === b ? 'bg-white shadow-sm' : 'text-slate-600')}>
                By {b}
              </button>
            ))}
          </div>
        }
      />
      <ErrorBox error={error} />
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Stat label={`Received ${year}`} value={money(totals.receivedYtd, false)} />
        <Stat label="Pipeline (weighted)" value={money(totals.pipelineWeighted, false)} sub="unreceived × stage probability" />
        <Stat label="Pipeline (if everything closes)" value={money(totals.pipelineFull, false)} />
      </div>
      <Card
        title={
          <span className="flex items-center gap-3">
            Weighted commission
            <span className="flex items-center gap-2 text-xs font-normal text-slate-500">
              <span className="inline-block h-2.5 w-2.5 rounded-sm bg-emerald-500" /> received
              <span className="inline-block h-2.5 w-2.5 rounded-sm bg-sky-500" /> invoiced
              <span className="inline-block h-2.5 w-2.5 rounded-sm bg-indigo-300" /> projected
            </span>
          </span>
        }
      >
        {buckets.length === 0 ? (
          <Empty>No payments yet. Revenue appears once deals have commission terms.</Empty>
        ) : (
          <ul className="space-y-1">
            {buckets.map(([k, b]) => {
              const total = b.received + b.invoiced + b.projected;
              return (
                <li key={k}>
                  <button className="flex w-full items-center gap-3 rounded px-1 py-1 text-left text-sm hover:bg-slate-50" onClick={() => setOpen(open === k ? null : k)}>
                    <span className="w-24 shrink-0 text-slate-600">{bucketLabel(k)}</span>
                    <span className="flex h-4 flex-1 overflow-hidden rounded bg-slate-100">
                      <span className="bg-emerald-500" style={{ width: `${(b.received / max) * 100}%` }} />
                      <span className="bg-sky-500" style={{ width: `${(b.invoiced / max) * 100}%` }} />
                      <span className="bg-indigo-300" style={{ width: `${(b.projected / max) * 100}%` }} />
                    </span>
                    <span className="w-28 shrink-0 text-right font-medium text-slate-900">{money(total, false)}</span>
                  </button>
                  {open === k && (
                    <table className="mb-2 ml-28 w-[calc(100%-7rem)] text-xs">
                      <tbody className="divide-y divide-slate-100">
                        {b.lines
                          .sort((x, y) => Number(y.weighted_amount) - Number(x.weighted_amount))
                          .map((l) => (
                            <tr key={l.payment_id}>
                              <td className="py-1">
                                <Link to={`/deals/${l.deal_id}`} className="text-indigo-700 hover:underline">
                                  {l.deal_name}
                                </Link>{' '}
                                <span className="text-slate-400">{DEAL_TYPE_LABEL[l.deal_type]}</span>
                              </td>
                              <td className="py-1">
                                <Pill color={l.category === 'received' ? 'green' : l.category === 'invoiced' ? 'blue' : 'gray'}>{l.category}</Pill>
                              </td>
                              <td className="py-1 text-right text-slate-500">
                                {money(l.amount, false)}
                                {l.category !== 'received' && ` × ${Math.round(Number(l.probability) * 100)}%`}
                              </td>
                              <td className="py-1 text-right font-medium">{money(l.weighted_amount, false)}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}
